/** Pure crop math for the Fill Mode quick-upload flow (unit-tested). */

export interface PercentCrop {
    unit: '%';
    x: number;
    y: number;
    width: number;
    height: number;
}

/** The largest centered crop of the image that matches the target frame's
 *  aspect ratio, as react-image-crop percent units. No frame → whole image. */
export const computeCenteredCrop = (
    imageWidth: number,
    imageHeight: number,
    frameWidth?: number,
    frameHeight?: number
): PercentCrop => {
    const full: PercentCrop = {unit: '%', x: 0, y: 0, width: 100, height: 100};
    if (!imageWidth || !imageHeight || !frameWidth || !frameHeight) return full;

    const imageAspect = imageWidth / imageHeight;
    const frameAspect = frameWidth / frameHeight;

    if (imageAspect > frameAspect) {
        // Image is wider than the frame: full height, trimmed width.
        const width = (frameAspect / imageAspect) * 100;
        return {unit: '%', x: (100 - width) / 2, y: 0, width, height: 100};
    }
    // Image is taller than the frame: full width, trimmed height.
    const height = (imageAspect / frameAspect) * 100;
    return {unit: '%', x: 0, y: (100 - height) / 2, width: 100, height};
};

/**
 * Tallest image any upload flow sends, in px.
 *
 * Designs run up to 1920 tall and a field box can exceed the board it sits on,
 * so the old 1080 cap meant the export UPSCALED the user's photo — the "soft
 * images" complaint. 2160 gives every current design a source at or above its
 * own resolution and still fits comfortably in the presigned PUT.
 *
 * Never UPSCALES: a source shorter than the cap keeps its own pixel dimensions
 * (it is still re-encoded through the canvas at those dimensions, so a JPEG is
 * re-compressed — the size is preserved, not the bytes) and keeps the mime type
 * it arrived in. Shared by the Fill Mode quick upload, the classic desktop
 * dialog and the AI result upload so the three cannot drift apart.
 */
export const MAX_UPLOAD_HEIGHT = 2160;

/** Output pixel size for a percent crop of a source image, downscaled so the
 *  result is at most `maxHeight` tall (MAX_UPLOAD_HEIGHT for every upload
 *  flow). Never upscales: a smaller source keeps its own dimensions. */
export const cropOutputSize = (
    imageWidth: number,
    imageHeight: number,
    crop: PercentCrop,
    maxHeight: number
): { width: number; height: number } => {
    let width = (crop.width / 100) * imageWidth;
    let height = (crop.height / 100) * imageHeight;
    if (height > maxHeight) {
        width = (maxHeight / height) * width;
        height = maxHeight;
    }
    return {width: Math.round(width), height: Math.round(height)};
};

/** Crop + downscale an already-loaded image element onto a canvas and return a
 *  Blob. Browser-only (canvas); the math above is what the tests cover. */
export const cropImageToBlob = (
    image: HTMLImageElement,
    crop: PercentCrop,
    mimeType: string,
    maxHeight: number
): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const {width, height} = cropOutputSize(image.width, image.height, crop, maxHeight);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas 2d context unavailable'));
        ctx.drawImage(
            image,
            (crop.x / 100) * image.width,
            (crop.y / 100) * image.height,
            (crop.width / 100) * image.width,
            (crop.height / 100) * image.height,
            0, 0, width, height
        );
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
            mimeType
        );
    });
